import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { memo, useEffect, useState } from "react";
import { z } from "zod";
import { ScreenSpinner } from "@/components/ui/spinner";
import { request } from "@/lib/request";
import { tokenManager } from "@/lib/auth/token-manager";
import { Team } from "@/lib/types";
import { TriangleAlertIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { LOCALSTORAGE_KEY } from "@/lib/constants";
import { JoinTeam } from "@/components/User/JoinTeam";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

const searchSchema = z.object({
  token: z.string().nonempty().catch(""),
});

class UnauthorizedError extends Error {}

export const Route = createFileRoute("/team/join")({
  validateSearch: searchSchema,
  loader: async () => {
    await tokenManager.ready;
    return { accessToken: tokenManager.getState().sessionKey };
  },
  component: memo(RouteComponent),
});

function RouteComponent() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const { accessToken: loaderAccessToken } = Route.useLoaderData();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [team, setTeam] = useState<Team | null>(null);

  const goBack = () => {
    navigate({
      to: "/",
      search: {
        change_password_token: undefined,
        reset_password_token: undefined,
      },
    });
  };

  const fetchTeamInfo = async () => {
    setLoading(true);
    setError(null);
    setTeam(null);
    captureEvent("onboarding_team_invite_loaded", {
      source: "route",
    });

    if (!loaderAccessToken || !tokenManager.isSignedIn()) {
      localStorage.setItem("unideck.auth.dismissed", "true");
      localStorage.setItem(`${LOCALSTORAGE_KEY}-join-team-token`, token);
      toast.info("Please log in and try opening the link again.");
      goBack();
      return;
    }

    try {
      const response = await request
        .get<{ data: Team }>(`/team/members/join/`, { params: { token } })
        .catch(err => {
          if (err instanceof AxiosError && err.response?.status === 401) {
            throw new UnauthorizedError();
          }

          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to fetch team information.",
          );
        });

      setTeam(response.data.data);
    } catch (err) {
      captureEvent("onboarding_team_invite_failed", {
        source: "route",
        reason: sanitizeErrorReason(err),
      });
      if (err instanceof UnauthorizedError) {
        toast.error("Wrong account!", {
          description: "Please log in with the correct account.",
        });
        goBack();
      }

      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch team information.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-background flex min-h-screen w-screen items-center justify-center">
      {loading ? (
        <div className="flex flex-col gap-4">
          <ScreenSpinner />
          <span className="text-muted-foreground animate-pulse">
            Fetching account information
          </span>
        </div>
      ) : error || !team ? (
        <div className="flex flex-col items-center gap-4">
          <TriangleAlertIcon className="size-16 text-white" />
          <span className="text-white">
            {error
              ? error
              : !team
                ? "Failed to fetch team information."
                : "Something went wrong, please try logging in again"}
          </span>
          <Button variant="default" onClick={goBack}>
            Go back
          </Button>
        </div>
      ) : (
        <div className="border-border bg-card flex w-full max-w-[600px] flex-col gap-4 rounded-xl border p-4 shadow-xl">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg font-bold text-white">
              {`You are invited to join "${team.name}"`}
            </h3>
            <JoinTeam team={team} goBack={goBack} />
          </div>
        </div>
      )}
    </div>
  );
}
